/**
 * Selection Step Component
 *
 * Step 1 of the assessment wizard - select operator and SOP
 */

import { useMemo } from 'react';
import {
  User,
  FileText,
  Building2,
  ListChecks,
  Layers,
  CheckCircle2,
  ChevronDown,
  Search,
} from 'lucide-react';
import { useOperatorLookup } from '../../../hooks/useOperators';
import { useSopLookup } from '../../../hooks/useSOPs';
import type { SopDetail } from '@guild/shared';

interface SelectionStepProps {
  operatorId: string;
  sopId: string;
  sop: SopDetail | undefined;
  sopLoading: boolean;
  onChange: (operatorId: string, sopId: string) => void;
}

export function SelectionStep({
  operatorId,
  sopId,
  sop,
  sopLoading,
  onChange,
}: SelectionStepProps) {
  // Fetch lookup data
  const { data: operators, isLoading: operatorsLoading } = useOperatorLookup();
  const { data: sops, isLoading: sopsLoading } = useSopLookup();

  // Find selected items for display
  const selectedOperator = useMemo(() => {
    return operators?.find(op => op.id === operatorId);
  }, [operators, operatorId]);

  return (
    <div className="p-8 animate-in fade-in duration-300">
      <div className="max-w-2xl mx-auto space-y-8">
        {/* Section Header */}
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00674a]/10 to-[#c5e8c2]/30 flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-[#00674a]" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
            Select Operator & SOP
          </h2>
          <p className="text-gray-500 mt-2">
            Choose the operator to assess and the procedure to evaluate
          </p>
        </div>

        {/* Operator Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-gray-700">
            Operator <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <User className="w-5 h-5" />
            </div>
            <select
              value={operatorId}
              onChange={(e) => onChange(e.target.value, sopId)}
              disabled={operatorsLoading}
              className="w-full pl-12 pr-10 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              <option value="">Select an operator...</option>
              {operators?.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.fullName} ({op.employeeId})
                </option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <ChevronDown className="w-5 h-5" />
            </div>
          </div>

          {/* Selected operator badge */}
          {selectedOperator && (
            <div className="flex items-center gap-3 p-3 bg-[#c5e8c2]/20 rounded-xl border border-[#c5e8c2]/50 animate-in fade-in slide-in-from-top-2 duration-300">
              <div className="w-10 h-10 rounded-lg bg-[#00674a] flex items-center justify-center text-white font-bold text-sm">
                {selectedOperator.fullName.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900">{selectedOperator.fullName}</p>
                <p className="text-sm text-gray-500">{selectedOperator.employeeId}</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-[#00674a]" />
            </div>
          )}
        </div>

        {/* SOP Selection */}
        <div className="space-y-3">
          <label className="block text-sm font-semibold text-gray-700">
            Standard Operating Procedure <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <FileText className="w-5 h-5" />
            </div>
            <select
              value={sopId}
              onChange={(e) => onChange(operatorId, e.target.value)}
              disabled={sopsLoading}
              className="w-full pl-12 pr-10 py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 bg-white appearance-none cursor-pointer disabled:bg-gray-50 disabled:cursor-not-allowed"
            >
              <option value="">Select an SOP...</option>
              {sops?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.title} (v{s.version})
                </option>
              ))}
            </select>
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <ChevronDown className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* SOP Preview Card */}
        {sopId && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            {sopLoading ? (
              <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                <div className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-2 border-[#00674a] border-t-transparent rounded-full animate-spin" />
                  <span className="text-gray-500">Loading SOP details...</span>
                </div>
              </div>
            ) : sop ? (
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00674a] to-[#00674a]/80 flex items-center justify-center flex-shrink-0 shadow-md">
                      <FileText className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 text-lg">{sop.title}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-gray-100 rounded text-xs font-semibold text-gray-600">
                          v{sop.version}
                        </span>
                        {sop.department && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <Building2 className="w-3 h-3" />
                            {sop.department.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-[#00674a]" />
                  </div>
                </div>

                {/* SOP Stats */}
                <div className="grid grid-cols-3 divide-x divide-gray-100">
                  <div className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-[#00674a]">
                      <Layers className="w-4 h-4" />
                      <span className="text-2xl font-bold">{sop.jobCount || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-medium">Jobs</p>
                  </div>
                  <div className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-[#00674a]">
                      <ListChecks className="w-4 h-4" />
                      <span className="text-2xl font-bold">{sop.taskCount || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-medium">Tasks</p>
                  </div>
                  <div className="p-4 text-center">
                    <div className="flex items-center justify-center gap-1.5 text-[#00674a]">
                      <CheckCircle2 className="w-4 h-4" />
                      <span className="text-2xl font-bold">{sop.stepCount || 0}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-medium">Steps</p>
                  </div>
                </div>

                {/* Warning if no steps */}
                {(sop.stepCount || 0) === 0 && (
                  <div className="p-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-sm text-amber-700 text-center">
                      This SOP has no steps defined. Please select a processed SOP.
                    </p>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* Ready indicator */}
        {operatorId && sopId && sop && (sop.stepCount || 0) > 0 && (
          <div className="flex items-center justify-center gap-2 p-4 bg-[#c5e8c2]/20 rounded-xl border border-[#c5e8c2]/50 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <CheckCircle2 className="w-5 h-5 text-[#00674a]" />
            <span className="font-semibold text-[#00674a]">
              Ready to proceed to scoring
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
